import {
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import { useObservableFocus } from '@/hooks/focus-hooks'
import { NEVER, type Observable } from 'rxjs'
import { type ControlStatus } from '@/components/inputs/control-status'
import {
  type ControlSize,
  useControlSize,
} from '@/components/inputs/control-size'
import { match } from '@/utils/pattern-match-util'
import styles from './TextInput.module.css'

export interface TextInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'disabled' | 'size'
> {
  isDisabled?: boolean
  staticText?: string
  status?: ControlStatus
  /**
   * Defaults to the size of the surrounding `ControlSizeContext`.
   */
  size?: ControlSize
  value?: string
  focus$?: Observable<unknown>

  onFocus?(e: FocusEvent<HTMLDivElement>): unknown

  onBlur?(e: FocusEvent<HTMLDivElement>): unknown

  /**
   * Only `color` changes the control, adding a swatch of the entered hex
   * color; the input stays a native text input for every type.
   */
  controlType?: 'text' | 'password' | 'color' | 'email' | 'tel'

  /**
   * Optionally adds a control button to the text input field.
   */
  button?: ReactNode

  /**
   * Optionally adds an avatar or icon to the text input field.
   * (Adding any other element type that is not an avatar/icon, will mess with the styling)
   */
  visual?: ReactNode
}

/**
 * A single-line text input.
 */
export function TextInput({
  visual,
  staticText,
  className,
  value,
  onChange,
  onFocus,
  onBlur,
  focus$ = NEVER,
  controlType = 'text',
  button,
  status = 'default',
  size: sizeProp,
  isDisabled,
  ...props
}: TextInputProps) {
  const size = useControlSize(sizeProp)
  const [focus, setFocus] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const color = value || ''
  const validColor = /^#[0-9A-F]{6}$/i.test(color)
  const focusInput = useCallback(() => inputRef.current?.focus(), [])
  useObservableFocus(focus$, focusInput)
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- mouse convenience around the input, which is the keyboard target
    <div
      className={cx(
        styles.textInputWrapper,
        match(size, {
          normal: undefined,
          compact: styles.compact,
          small: styles.small,
        }),
        isDisabled && styles.disabled,
        controlType === 'color' && styles.colorInput,
        Boolean(button) && styles.withButton,
        styles[status],
        focus && styles.focus,
      )}
      onClick={focusInput}
    >
      {controlType === 'color' && (
        <div
          className={styles.colorHint}
          style={{ backgroundColor: validColor ? color : 'transparent' }}
        />
      )}
      {visual && <div className={styles.withVisual}>{visual}</div>}
      <input
        ref={inputRef}
        type="text"
        className={cx(styles.input, className)}
        disabled={isDisabled}
        {...props}
        onFocus={(e) => {
          setFocus(true)
          onFocus?.(e)
        }}
        onBlur={(e) => {
          setFocus(false)
          onBlur?.(e)
        }}
        value={value}
        onChange={onChange}
      />
      {staticText && <div className={styles.inputStaticText}>{staticText}</div>}
      {button}
    </div>
  )
}
